import isEmpty from 'lodash/isEmpty';
import type { AriaAttributes, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { getActionAriaLabelSync } from '@/utils/ariaLabels';

import { Overlay } from '@/components/Overlay';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll';
import type { WithChildren, WithHTMLProp } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { BreakpointsKeys } from '@/theme/types';

import * as S from './Modal.styles';

export interface ModalProps extends Pick<
  AriaAttributes,
  'aria-label' | 'aria-labelledby'
> {
  alignItems?: string;
  children?: ReactNode;
  className?: string;
  ecsClose?: boolean;
  focusFirstElement?: boolean;
  gutters?: boolean;
  height?: string;
  isCreatePortal?: boolean;
  isOpen?: boolean;
  maxWidth?: BreakpointsKeys;
  mobileScreenClose?: boolean;
  onClose?: () => void;
  overflowHidden?: boolean;
  rootId?: string;
  screenClose?: boolean;
  scrollToTopWhenOpen?: boolean;
  unmountOnClose?: boolean;
  width?: string;
}

export const Modal = ({
  alignItems,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
  className,
  ecsClose = true,
  focusFirstElement = true,
  gutters = true,
  height = '',
  isCreatePortal = true,
  isOpen = false,
  maxWidth = 'sm',
  mobileScreenClose = false,
  onClose,
  overflowHidden = false,
  rootId = 'modal-root',
  screenClose = true,
  scrollToTopWhenOpen,
  unmountOnClose = true,
  width = '',
}: ModalProps) => {
  const modalTargetElement = useRef<HTMLElement | null>(null);
  const { isMobile } = useDeviceDetect();
  const isOverlayClose = screenClose && (!isMobile || mobileScreenClose);

  const modalContentRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalContentRef, isOpen, focusFirstElement);

  useEffect(() => {
    if (isCreatePortal) {
      modalTargetElement.current = document.getElementById(rootId);
    }
  }, [isCreatePortal]);

  useLockBodyScroll(isOpen, scrollToTopWhenOpen);

  useEffect(() => {
    if (!ecsClose) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [ecsClose, isOpen]);

  const portalContent = (
    <Overlay
      alignItems={alignItems}
      isFullWidthMobile={isEmpty(width)}
      onClick={isOverlayClose ? onClose : undefined}
    >
      <S.Wrapper
        ref={modalContentRef}
        $height={height}
        $maxWidth={maxWidth}
        $overflowHidden={overflowHidden}
        $width={width}
        aria-hidden={!isOpen || undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-modal
        className={className}
        gutters={gutters}
        role='dialog'
        tabIndex={-1}
      >
        {children}
      </S.Wrapper>
    </Overlay>
  );

  if (!isCreatePortal) {
    return (
      <Overlay
        alignItems={alignItems}
        onClick={onClose}
        style={{
          pointerEvents: isOpen ? 'all' : 'none',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <S.Wrapper
          ref={modalContentRef}
          $height={height}
          $maxWidth={maxWidth}
          $overflowHidden={overflowHidden}
          $width={width}
          aria-hidden={!isOpen || undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-modal
          className={className}
          gutters={gutters}
          role='dialog'
          style={{
            isolation: 'isolate',
            pointerEvents: isOpen ? 'all' : 'none',
            opacity: isOpen ? 1 : 0,
          }}
          tabIndex={-1}
        >
          {children}
        </S.Wrapper>
      </Overlay>
    );
  }

  // TODO: Refactor it better
  if (!unmountOnClose && modalTargetElement.current) {
    return createPortal(
      <Overlay
        alignItems={alignItems}
        onClick={isOverlayClose ? onClose : undefined}
        style={{
          pointerEvents: isOpen ? 'all' : 'none',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <S.Wrapper
          ref={modalContentRef}
          $height={height}
          $maxWidth={maxWidth}
          $overflowHidden={overflowHidden}
          $width={width}
          aria-hidden={!isOpen || undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-modal
          className={className}
          gutters={gutters}
          role='dialog'
          style={{
            isolation: 'isolate',
            pointerEvents: isOpen ? 'all' : 'none',
            opacity: isOpen ? 1 : 0,
          }}
          tabIndex={-1}
        >
          {children}
        </S.Wrapper>
      </Overlay>,
      modalTargetElement.current,
    );
  }

  if (!modalTargetElement.current || !isOpen) {
    return null;
  }

  return createPortal(portalContent, modalTargetElement.current);
};

interface ModalHeaderProps {
  children?: ReactNode;
  className?: string;
  divider?: boolean;
  onClose?: () => void;
}

const ModalHeader = ({
  children,
  className,
  divider,
  onClose,
}: ModalHeaderProps) => {
  const { locale } = useRegionProvider();
  return (
    <>
      <S.Header
        className={className ? `ModalHeader ${className}` : 'ModalHeader'}
        data-testid='modalHeader'
      >
        {children}
        {onClose && (
          <S.Close
            aria-label={
              getActionAriaLabelSync('close_modal', locale.code) ||
              'Close modal'
            }
            data-testid='modalClose'
            iconProps={{ name: 'close', variant: 'regular' }}
            onClick={onClose}
            size='s'
            variant='ghosted'
          />
        )}
      </S.Header>
      {divider && <S.Divider />}
    </>
  );
};

type ModalBodyProps = WithChildren & WithHTMLProp<'className'>;
const ModalBody = ({ children, className }: ModalBodyProps) => (
  <S.Body className={className} data-testid='modalBody'>
    {children}
  </S.Body>
);

type ModalFooterProps = WithChildren &
  WithHTMLProp<'className'> & { isFullWidthMobile?: boolean };
const ModalFooter = ({
  children,
  className,
  isFullWidthMobile = true,
}: ModalFooterProps) => (
  <S.Footer className={className} isFullWidthMobile={isFullWidthMobile}>
    {children}
  </S.Footer>
);

type ModalTailProps = WithChildren & WithHTMLProp<'className'>;
const ModalTail = ({ children, className }: ModalTailProps) => (
  <S.TailWrapper className={className}>
    {children}
    <S.Tail />
  </S.TailWrapper>
);

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Tail = ModalTail;

Modal.displayName = 'Modal';
